import * as React from 'react'
import { MailWarning } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { isVendor } from '@/auth/roles'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useOwnVendor } from '@/hooks/useVendors'
import { authService } from '@/services/AuthService'
import { FormError } from '@/components/ui/form-error'

/**
 * The strip across the top of a vendor's pages while the company it signed up
 * waits for its email to be confirmed (docs/public-site.spec.md, "Vendor
 * signup"): what's held back until then, and a way to get a link. Anyone at the
 * company sees it, including people invited after signup who were never sent
 * one, so it says nothing about a link already sent; the link it sends goes to
 * whoever asks, and any of them can confirm.
 */
export function EmailConfirmationBanner() {
  const { user } = useAuth()
  const vendor = useOwnVendor({ enabled: isVendor(user) }).data
  const [state, setState] = React.useState<'idle' | 'sending' | 'sent'>('idle')
  const [error, setError] = React.useState<string | null>(null)
  if (!vendor?.awaitingEmailConfirmation || user === null) return null

  async function resend(): Promise<void> {
    setState('sending')
    setError(null)
    try {
      await authService.resendEmailConfirmation()
      setState('sent')
    } catch (cause) {
      setError(requestErrorMessage(cause))
      setState('idle')
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line bg-hold-soft px-4 py-2 text-sm text-hold-ink">
      <MailWarning className="h-4 w-4 shrink-0" aria-hidden />
      <span>Confirm your email to ask venues to work with you and to appear in their search.</span>
      {state === 'sent' ? (
        <span>We sent a link to {user.email}.</span>
      ) : (
        <button type="button" className="font-medium underline underline-offset-2 disabled:opacity-60" disabled={state === 'sending'} onClick={() => void resend()}>
          Send me a link
        </button>
      )}
      {/* In danger ink, not the banner's own: a failure has to stand out from the request it failed. */}
      {error === null ? null : (
        <FormError size="sm" className="basis-full">
          {error}
        </FormError>
      )}
    </div>
  )
}
